'use client';

import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { motion } from 'framer-motion';
import { tv } from 'tailwind-variants';

import { cn } from '@/utils/cn-tw-merge';

export type TabsVariant = 'default' | 'green';

const INDICATOR_TRANSITION = { type: 'spring', stiffness: 300, damping: 30 } as const;

interface TabsState {
  activeTab: string;
  layoutId: string;
}

const TabsStateContext = React.createContext<TabsState | null>(null);
const TabsVariantContext = React.createContext<TabsVariant>('default');

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(({ value, defaultValue, onValueChange, ...props }, ref) => {
  const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue ?? '');
  const layoutId = React.useId();

  const handleValueChange = React.useCallback(
    (next: string) => {
      if (value === undefined) {
        setUncontrolledValue(next);
      }
      onValueChange?.(next);
    },
    [value, onValueChange],
  );

  const state = React.useMemo<TabsState>(
    () => ({ activeTab: value ?? uncontrolledValue, layoutId }),
    [value, uncontrolledValue, layoutId],
  );

  return (
    <TabsStateContext.Provider value={state}>
      <TabsPrimitive.Root
        ref={ref}
        value={value}
        defaultValue={defaultValue}
        onValueChange={handleValueChange}
        {...props}
      />
    </TabsStateContext.Provider>
  );
});

Tabs.displayName = TabsPrimitive.Root.displayName;

const tabsList = tv({
  base: 'inline-flex items-center justify-center rounded-lg border p-1 relative ',
  variants: {
    variant: {
      default: 'bg-gray-100 border-gray-300 text-gray-700',
      green: 'bg-green-100 border-green-100 text-green-500',
    },
  },
});

const tabIndicator = tv({
  base: 'absolute inset-0 rounded-sm',
  variants: {
    variant: {
      default: 'bg-gray-700',
      green: 'bg-green-500',
    },
  },
});

const tabTrigger = tv({
  base: [
    'relative inline-flex h-9 items-center justify-center whitespace-nowrap rounded-sm px-4 text-lg',
    'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50 bg-transparent z-20',
    'data-[state=active]:font-semibold data-[state=active]:shadow-sm',
  ],
  variants: {
    variant: {
      default: 'ring-offset-white focus-visible:ring-slate-950 text-gray-700 data-[state=active]:text-white',
      green: 'ring-offset-green-100 focus-visible:ring-green-500 text-green-500 data-[state=active]:text-white',
    },
  },
});

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    variant?: TabsVariant;
    contentClassName?: string;
  }
>(({ className, contentClassName, variant = 'default', children, ...props }, ref) => (
  <TabsVariantContext.Provider value={variant}>
    <TabsPrimitive.List ref={ref} className={cn(tabsList({ variant }), className)} {...props}>
      <div className={cn('relative z-10 flex flex-row', contentClassName)}>{children}</div>
    </TabsPrimitive.List>
  </TabsVariantContext.Provider>
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & { variant?: TabsVariant }
>(({ className, variant, children, ...props }, ref) => {
  const state = React.useContext(TabsStateContext);
  const listVariant = React.useContext(TabsVariantContext);
  const resolvedVariant = variant ?? listVariant;

  return (
    <TabsPrimitive.Trigger ref={ref} className={cn(tabTrigger({ variant: resolvedVariant }), className)} {...props}>
      {state?.activeTab === props.value && (
        <motion.div
          layoutId={state.layoutId}
          className={tabIndicator({ variant: resolvedVariant })}
          initial={false}
          transition={INDICATOR_TRANSITION}
        />
      )}
      <span className="relative z-10">{children}</span>
    </TabsPrimitive.Trigger>
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'data-[state=inactive]:hidden ring-offset-white focus-visible:outline-none focus-visible:ring-2',
      'focus-visible:ring-slate-950 focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
